import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, FileText, Plus, Save, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import Swal from 'sweetalert2'

import type { AdjustmentBatchRow } from '@/services/inventoryAdjustmentService'
import {
  bonusDateInputToApiIso,
  bonusSchemeService,
  extractBonusMasterIdFromResponse,
  type BonusSchemeSavePayload,
} from '@/services/bonusSchemeService'
import { useAuthStore } from '@/store/auth.store'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { BonusItemLine } from '../types'
import { newBonusItemLine } from '../types'
import { nextBonusItemCode } from '../storage'

const SELECT_EMPTY = '__bonus_empty__'
const SELECT_ANY = '0'
const SELECT_ACTIVE = '0'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

export const BonusItemsEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const selectedBranchId = Number(user?.selectedBranch?.branchId ?? 0)
  const prevBranchIdRef = useRef(0)

  const recordId = id && id !== 'new' ? id : ''
  const isNew = !recordId

  const [docDate, setDocDate] = useState(todayIso())
  const [startDate, setStartDate] = useState(todayIso())
  const [endDate, setEndDate] = useState(todayIso())
  const [code, setCode] = useState('')

  const [itemOptions, setItemOptions] = useState<{ value: string; label: string }[]>([])
  const [itemsLoading, setItemsLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  /** Server master id: 0 = POST Save, &gt; 0 = PUT Update */
  const [serverBonusId, setServerBonusId] = useState(0)

  const [lines, setLines] = useState<BonusItemLine[]>(() =>
    Array.from({ length: 3 }, () => newBonusItemLine())
  )

  const [batchesPromo, setBatchesPromo] = useState<Record<string, AdjustmentBatchRow[]>>({})
  const [batchesBonus, setBatchesBonus] = useState<Record<string, AdjustmentBatchRow[]>>({})
  const [batchesLoadingPromo, setBatchesLoadingPromo] = useState<Record<string, boolean>>({})
  const [batchesLoadingBonus, setBatchesLoadingBonus] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (isNew || !recordId) return
    const masterId = Number(recordId)
    if (!Number.isFinite(masterId) || masterId <= 0) {
      toast.error(t('bonusItems.notFound'))
      navigate('/inventory/schemes-discounts/bonus-items')
      return
    }
    if (!selectedBranchId) return

    let cancelled = false
    setDetailLoading(true)
    void Promise.all([
      bonusSchemeService.getById(selectedBranchId, masterId),
      bonusSchemeService.getLinesByMasterId(selectedBranchId, masterId),
    ])
      .then(([master, lineRows]) => {
        if (cancelled) return
        if (master.id <= 0) {
          toast.error(t('bonusItems.notFound'))
          navigate('/inventory/schemes-discounts/bonus-items')
          return
        }
        setServerBonusId(master.id)
        setCode(master.code)
        setDocDate(master.date || todayIso())
        setStartDate(master.schemeStartDate || todayIso())
        setEndDate(master.schemeEndDate || todayIso())
        setLines(
          lineRows.length > 0 ? lineRows : Array.from({ length: 3 }, () => newBonusItemLine())
        )
        setBatchesPromo({})
        setBatchesBonus({})
      })
      .catch((e: unknown) => {
        if (cancelled) return
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined
        toast.error(msg || t('bonusItems.detailLoadFailed'))
        navigate('/inventory/schemes-discounts/bonus-items')
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isNew, recordId, selectedBranchId, navigate, t])

  useEffect(() => {
    if (isNew && !code) {
      setCode(nextBonusItemCode())
    }
  }, [isNew, code])

  useEffect(() => {
    if (!selectedBranchId) {
      setItemOptions([])
      setBatchesPromo({})
      setBatchesBonus({})
      setBatchesLoadingPromo({})
      setBatchesLoadingBonus({})
      return
    }

    if (prevBranchIdRef.current !== 0 && prevBranchIdRef.current !== selectedBranchId) {
      setBatchesPromo({})
      setBatchesBonus({})
      setBatchesLoadingPromo({})
      setBatchesLoadingBonus({})
      setServerBonusId(0)
      setLines((prev) =>
        prev.map((l) => ({
          ...l,
          promotionItemId: '',
          promotionBatchId: '',
          bonusItemId: '',
          bonusBatchId: '',
          serverLineId: undefined,
        }))
      )
    }
    prevBranchIdRef.current = selectedBranchId

    let cancelled = false
    setItemsLoading(true)
    void bonusSchemeService
      .getAvailableItems(selectedBranchId)
      .then((opts) => {
        if (!cancelled) setItemOptions(opts)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setItemOptions([])
          const msg =
            e && typeof e === 'object' && 'response' in e
              ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
              : undefined
          toast.error(msg || t('unsellableInventory.itemsLoadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setItemsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedBranchId, t])

  const loadBatches = (lineId: string, branchId: number, itemIdNum: number, side: 'promo' | 'bonus') => {
    if (side === 'promo') {
      setBatchesLoadingPromo((prev) => ({ ...prev, [lineId]: true }))
    } else {
      setBatchesLoadingBonus((prev) => ({ ...prev, [lineId]: true }))
    }
    void bonusSchemeService
      .getAvailableBatches(branchId, itemIdNum)
      .then((rows) => {
        if (side === 'promo') {
          setBatchesPromo((prev) => ({ ...prev, [lineId]: rows }))
        } else {
          setBatchesBonus((prev) => ({ ...prev, [lineId]: rows }))
        }
      })
      .catch(() => {
        toast.error(t('unsellableInventory.batchesLoadFailed'))
        if (side === 'promo') {
          setBatchesPromo((prev) => ({ ...prev, [lineId]: [] }))
        } else {
          setBatchesBonus((prev) => ({ ...prev, [lineId]: [] }))
        }
      })
      .finally(() => {
        if (side === 'promo') {
          setBatchesLoadingPromo((prev) => ({ ...prev, [lineId]: false }))
        } else {
          setBatchesLoadingBonus((prev) => ({ ...prev, [lineId]: false }))
        }
      })
  }

  const linesBatchKey = useMemo(
    () => lines.map((l) => `${l.id}|${l.promotionItemId}|${l.bonusItemId}`).join(';;'),
    [lines]
  )

  useEffect(() => {
    if (!selectedBranchId) return
    for (const line of lines) {
      const pi = Number(line.promotionItemId)
      if (pi) {
        const existing = batchesPromo[line.id]
        if (!existing || existing.length === 0) {
          loadBatches(line.id, selectedBranchId, pi, 'promo')
        }
      }
      const bi = Number(line.bonusItemId)
      if (bi) {
        const existing = batchesBonus[line.id]
        if (!existing || existing.length === 0) {
          loadBatches(line.id, selectedBranchId, bi, 'bonus')
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate batches when line items / branch change
  }, [linesBatchKey, selectedBranchId])

  const handlePromoItemChange = (index: number, value: string) => {
    let lineId = ''
    setLines((prev) => {
      const next = [...prev]
      lineId = next[index].id
      if (value === SELECT_EMPTY) {
        next[index] = {
          ...next[index],
          promotionItemId: '',
          promotionBatchId: '',
          promotionQty: '',
        }
      } else {
        next[index] = {
          ...next[index],
          promotionItemId: value,
          promotionBatchId: SELECT_ANY,
          promotionQty: next[index].promotionQty,
        }
      }
      return next
    })
    setBatchesPromo((prev) => ({ ...prev, [lineId]: [] }))
    if (value === SELECT_EMPTY) return
    const itemNum = Number(value)
    if (selectedBranchId && itemNum) loadBatches(lineId, selectedBranchId, itemNum, 'promo')
  }

  const handleBonusItemChange = (index: number, value: string) => {
    let lineId = ''
    setLines((prev) => {
      const next = [...prev]
      lineId = next[index].id
      if (value === SELECT_EMPTY) {
        next[index] = {
          ...next[index],
          bonusItemId: '',
          bonusBatchId: '',
          bonusQty: '',
        }
      } else {
        next[index] = {
          ...next[index],
          bonusItemId: value,
          bonusBatchId: SELECT_ACTIVE,
          bonusQty: next[index].bonusQty,
        }
      }
      return next
    })
    setBatchesBonus((prev) => ({ ...prev, [lineId]: [] }))
    if (value === SELECT_EMPTY) return
    const itemNum = Number(value)
    if (selectedBranchId && itemNum) loadBatches(lineId, selectedBranchId, itemNum, 'bonus')
  }

  const addLines = (n: number) => {
    setLines((prev) => [...prev, ...Array.from({ length: n }, () => newBonusItemLine())])
  }

  const removeLine = (index: number) => {
    setLines((prev) => {
      if (prev.length <= 1) return prev
      const removed = prev[index]
      const next = prev.filter((_, i) => i !== index)
      setBatchesPromo((b) => {
        const { [removed.id]: _, ...rest } = b
        return rest
      })
      setBatchesBonus((b) => {
        const { [removed.id]: _, ...rest } = b
        return rest
      })
      return next
    })
  }

  const handleBack = () => navigate('/inventory/schemes-discounts/bonus-items')

  const handleSave = async () => {
    if (!selectedBranchId) {
      toast.error(t('unsellableInventory.saveNoBranch'))
      return
    }

    const filled = lines.filter(
      (l) =>
        l.promotionItemId &&
        l.promotionBatchId !== '' &&
        l.promotionQty.trim() !== '' &&
        l.bonusItemId &&
        l.bonusBatchId !== '' &&
        l.bonusQty.trim() !== ''
    )
    if (filled.length === 0) {
      toast.error(t('bonusItems.validationLines'))
      return
    }

    const masterIdForPayload = serverBonusId
    const itemList = filled.map((l) => ({
      id: l.serverLineId != null && l.serverLineId > 0 ? l.serverLineId : 0,
      bonusId: masterIdForPayload,
      promotionItemId: Number(l.promotionItemId),
      promotionBatchId: Number(l.promotionBatchId),
      purchaseQuantity: Number(l.promotionQty) || 0,
      bonusItemId: Number(l.bonusItemId),
      bonusBatchId: Number(l.bonusBatchId),
      bonusQuantity: Number(l.bonusQty) || 0,
      isDeleted: 'false',
    }))

    const payload: BonusSchemeSavePayload = {
      id: masterIdForPayload,
      code: (code.trim() || nextBonusItemCode()).trim(),
      date: bonusDateInputToApiIso(docDate),
      schemeStartDate: bonusDateInputToApiIso(startDate),
      schemeEndDate: bonusDateInputToApiIso(endDate),
      branchId: selectedBranchId,
      itemList,
    }

    console.log('[Bonus] Save/Update request body (object):', payload)
    console.log('[Bonus] Save/Update request body (JSON):', JSON.stringify(payload, null, 2))

    setSaveLoading(true)
    try {
      const res =
        masterIdForPayload > 0
          ? await bonusSchemeService.update(payload)
          : await bonusSchemeService.save(payload)

      console.log('[Bonus] API response body (object):', res.data)
      console.log('[Bonus] API response body (JSON):', JSON.stringify(res.data, null, 2))
      console.log('[Bonus] API full axios response:', res)

      const nextMasterId = extractBonusMasterIdFromResponse(res.data) || masterIdForPayload
      if (nextMasterId > 0) setServerBonusId(nextMasterId)

      await Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('bonusItems.saveSuccess'),
        confirmButtonText: t('common.ok'),
      })
      navigate('/inventory/schemes-discounts/bonus-items')
    } catch (err: unknown) {
      let msg = ''
      if (err && typeof err === 'object' && 'response' in err) {
        const data = (err as { response?: { data?: unknown } }).response?.data
        if (typeof data === 'string') msg = data
        else if (data && typeof data === 'object') {
          const d = data as Record<string, unknown>
          msg = String(d.message ?? d.Message ?? d.msg ?? d.Msg ?? '')
        }
      }
      if (!msg.trim() && err instanceof Error) msg = err.message
      await Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: msg.trim() || t('bonusItems.saveFailed'),
        confirmButtonText: t('common.ok'),
      })
    } finally {
      setSaveLoading(false)
    }
  }

  const tableHeaderClass =
    'h-11 border-0 bg-primary/5 text-xs font-semibold uppercase tracking-wide text-primary hover:bg-primary/5 dark:bg-primary/10 dark:text-primary'

  if (!isNew && detailLoading) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 p-8">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <FileText className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('bonusItems.formTitle')}</h1>
          <p className="text-muted-foreground text-sm">{t('bonusItems.formSubtitle')}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('bonusItems.sectionHeader')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="bonus-date">{t('bonusItems.date')}</Label>
            <Input
              id="bonus-date"
              type="date"
              value={docDate}
              onChange={(e) => setDocDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="bonus-start">{t('bonusItems.startDate')}</Label>
            <Input
              id="bonus-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="bonus-end">{t('bonusItems.endDate')}</Label>
            <Input
              id="bonus-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-lg">{t('bonusItems.sectionLines')}</CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => addLines(1)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('bonusItems.addRow')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => addLines(3)}>
              {t('bonusItems.add3Rows')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => addLines(5)}>
              {t('bonusItems.add5Rows')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 sm:p-6 sm:pt-0">
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className={tableHeaderClass}>{t('bonusItems.colPromotionItem')}</TableHead>
                  <TableHead className={tableHeaderClass}>{t('bonusItems.colPromotionBatch')}</TableHead>
                  <TableHead className={`${tableHeaderClass} text-center`}>
                    {t('bonusItems.colPromotionQty')}
                  </TableHead>
                  <TableHead className={tableHeaderClass}>{t('bonusItems.colBonusItem')}</TableHead>
                  <TableHead className={tableHeaderClass}>{t('bonusItems.colBonusBatch')}</TableHead>
                  <TableHead className={`${tableHeaderClass} text-center`}>
                    {t('bonusItems.colBonusQty')}
                  </TableHead>
                  <TableHead className={`${tableHeaderClass} w-[56px] text-center`} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line, index) => {
                  const promoLoading = batchesLoadingPromo[line.id]
                  const bonusLoading = batchesLoadingBonus[line.id]
                  const promoList = batchesPromo[line.id] ?? []
                  const bonusList = batchesBonus[line.id] ?? []
                  const branchOk = Boolean(selectedBranchId)

                  return (
                    <TableRow key={line.id}>
                      <TableCell className="min-w-[200px] align-top">
                        <Select
                          value={line.promotionItemId || SELECT_EMPTY}
                          onValueChange={(v) => handlePromoItemChange(index, v)}
                          disabled={!branchOk || itemsLoading}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={t('bonusItems.selectItem')} />
                          </SelectTrigger>
                          <SelectContent searchable>
                            <SelectItem value={SELECT_EMPTY}>—</SelectItem>
                            {itemOptions.map((o) => (
                              <SelectItem key={`p-${line.id}-${o.value}`} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="min-w-[180px] align-top">
                        <Select
                          value={line.promotionBatchId || SELECT_ANY}
                          onValueChange={(v) =>
                            setLines((prev) => {
                              const next = [...prev]
                              next[index] = {
                                ...next[index],
                                promotionBatchId: v,
                              }
                              return next
                            })
                          }
                          disabled={!line.promotionItemId || promoLoading}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue
                              placeholder={
                                promoLoading ? t('common.loading') : t('bonusItems.selectBatch')
                              }
                            />
                          </SelectTrigger>
                          <SelectContent searchable>
                            <SelectItem value={SELECT_ANY}>
                              {t('bonusItems.batchAny', { defaultValue: 'Any' })}
                            </SelectItem>
                            {promoList.map((b) => (
                              <SelectItem key={`pb-${line.id}-${b.id}`} value={String(b.id)}>
                                {b.code || b.reference || String(b.id)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="w-[120px] align-top">
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          className="h-9"
                          value={line.promotionQty}
                          onChange={(e) =>
                            setLines((prev) => {
                              const next = [...prev]
                              next[index] = { ...next[index], promotionQty: e.target.value }
                              return next
                            })
                          }
                        />
                      </TableCell>
                      <TableCell className="min-w-[200px] align-top">
                        <Select
                          value={line.bonusItemId || SELECT_EMPTY}
                          onValueChange={(v) => handleBonusItemChange(index, v)}
                          disabled={!branchOk || itemsLoading}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={t('bonusItems.selectItem')} />
                          </SelectTrigger>
                          <SelectContent searchable>
                            <SelectItem value={SELECT_EMPTY}>—</SelectItem>
                            {itemOptions.map((o) => (
                              <SelectItem key={`b-${line.id}-${o.value}`} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="min-w-[180px] align-top">
                        <Select
                          value={line.bonusBatchId || SELECT_ACTIVE}
                          onValueChange={(v) =>
                            setLines((prev) => {
                              const next = [...prev]
                              next[index] = {
                                ...next[index],
                                bonusBatchId: v,
                              }
                              return next
                            })
                          }
                          disabled={!line.bonusItemId || bonusLoading}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue
                              placeholder={
                                bonusLoading ? t('common.loading') : t('bonusItems.selectBatch')
                              }
                            />
                          </SelectTrigger>
                          <SelectContent searchable>
                            <SelectItem value={SELECT_ACTIVE}>
                              {t('bonusItems.batchActive', { defaultValue: 'Active Batch' })}
                            </SelectItem>
                            {bonusList.map((b) => (
                              <SelectItem key={`bb-${line.id}-${b.id}`} value={String(b.id)}>
                                {b.code || b.reference || String(b.id)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="w-[120px] align-top">
                        <Input
                          type="number"
                          min={0}
                          step="0.01"
                          className="h-9"
                          value={line.bonusQty}
                          onChange={(e) =>
                            setLines((prev) => {
                              const next = [...prev]
                              next[index] = { ...next[index], bonusQty: e.target.value }
                              return next
                            })
                          }
                        />
                      </TableCell>
                      <TableCell className="align-top text-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          onClick={() => removeLine(index)}
                          disabled={lines.length <= 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-2">
        <Button type="button" variant="outline" onClick={handleBack} className="w-full sm:w-auto">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('bonusItems.back')}
        </Button>
        <Button
          type="button"
          onClick={() => void handleSave()}
          disabled={saveLoading}
          className="w-full sm:w-auto"
        >
          <Save className="mr-2 h-4 w-4" />
          {saveLoading
            ? t('common.saving', { defaultValue: 'Saving…' })
            : !isNew
              ? t('common.update')
              : t('bonusItems.save')}
        </Button>
      </div>
    </div>
  )
}
